// apps/client/src/components/SharablePage/SharePageAnalytics.tsx
import { ShareDailyViewsChart, DailyPoint } from './ShareDailyViewsChart'
import { useTar } from '@/modules'

export interface SharePageAnalyticsData {
  totalViews: number
  last7Days: number
  dailySeries: DailyPoint[]
  topSources: Array<{ _id: string; count: number }>
}

interface Props {
  data: SharePageAnalyticsData
  hasBeenPublished: boolean
}

export const SharePageAnalytics = ({ data, hasBeenPublished }: Props) => {
  const { t } = useTar()
  if (!hasBeenPublished) {
    return (
      <div className="text-center text-sm text-muted-foreground bg-muted/20 border border-dashed border-border rounded-lg py-7">
        {t('share.analytics.empty')}
      </div>
    )
  }
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="text-sm font-semibold mb-1">{t('share.analytics.title')}</div>
      <div className="text-xs text-muted-foreground mb-4">{t('share.analytics.subtitle')}</div>
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_220px] gap-4">
        <div className="flex flex-col gap-2.5">
          <StatBlock label={t('share.analytics.totalViews')} value={data.totalViews} />
          <StatBlock label={t('share.analytics.last7Days')} value={data.last7Days} small />
        </div>
        <div className="bg-muted/20 border border-border rounded-lg p-3">
          <div className="text-[11px] tracking-[1px] uppercase text-muted-foreground font-semibold mb-2">
            {t('share.analytics.dailyViews')}
          </div>
          <ShareDailyViewsChart series={data.dailySeries} />
        </div>
        <div className="bg-muted/20 border border-border rounded-lg p-3">
          <div className="text-[11px] tracking-[1px] uppercase text-muted-foreground font-semibold mb-2">
            {t('share.analytics.topSources')}
          </div>
          <div className="flex flex-col gap-1.5 text-sm">
            {data.topSources.length === 0 && (
              <div className="text-xs text-muted-foreground">{t('share.analytics.noSourcesYet')}</div>
            )}
            {data.topSources.map((s) => (
              <div key={s._id} className="flex justify-between">
                <span>{s._id}</span>
                <span className="text-muted-foreground">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

const StatBlock = ({ label, value, small }: { label: string; value: number; small?: boolean }) => (
  <div className="bg-muted/20 border border-border rounded-lg p-3">
    <div className="text-[10px] tracking-[1px] uppercase text-muted-foreground font-semibold">{label}</div>
    <div className={`font-serif font-bold leading-tight mt-1 ${small ? 'text-2xl' : 'text-3xl'}`}>{value}</div>
  </div>
)
